import React from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  X as ClearIcon,
  User,
  Hash,
  Phone,
  CreditCard,
} from 'lucide-react';

/**
 * Helper function to match customer search across:
 * - Refer ID (refNo)
 * - Name
 * - Contact numbers (contactNo1, contactNo2)
 * - Aadhar numbers (aadharNo1, aadharNo2)
 * - Location
 */
export const matchCustomerSearch = (entry, query) => {
  if (!query || !query.trim()) return true;
  const q = query.toLowerCase().trim();
  const cleanQ = q.replace(/[\s\-_]/g, '');

  const refNo = String(entry.refNo || '').toLowerCase();
  const name = String(entry.name || '').toLowerCase();
  const location = String(entry.location || '').toLowerCase();

  const contact1 = String(entry.contactNo1 || '').toLowerCase();
  const contact2 = String(entry.contactNo2 || '').toLowerCase();
  const cleanContact1 = contact1.replace(/[\s\-_]/g, '');
  const cleanContact2 = contact2.replace(/[\s\-_]/g, '');

  const aadhar1 = String(entry.aadharNo1 || '').toLowerCase();
  const aadhar2 = String(entry.aadharNo2 || '').toLowerCase();
  const cleanAadhar1 = aadhar1.replace(/[\s\-_]/g, '');
  const cleanAadhar2 = aadhar2.replace(/[\s\-_]/g, '');

  return (
    refNo.includes(q) ||
    name.includes(q) ||
    location.includes(q) ||
    contact1.includes(q) ||
    contact2.includes(q) ||
    (cleanQ.length >= 3 && (cleanContact1.includes(cleanQ) || cleanContact2.includes(cleanQ))) ||
    aadhar1.includes(q) ||
    aadhar2.includes(q) ||
    (cleanQ.length >= 3 && (cleanAadhar1.includes(cleanQ) || cleanAadhar2.includes(cleanQ)))
  );
};

/**
 * Universal Customer Search Bar Card Component
 */
const CustomerSearchBar = ({
  searchQuery,
  onSearchChange,
  totalCount = 0,
  filteredCount = 0,
  placeholder = "Search by Refer ID (e.g. VF-0001), Customer Name, Contact Number, or Aadhaar No...",
  title = "Universal Customer Search",
  subtitle = "Single search field matching Refer ID, Name, Phone & Aadhaar numbers in real-time",
  extraActions = null,
}) => {
  const isFiltered = Boolean(searchQuery && searchQuery.trim());

  return (
    <Card
      elevation={0}
      sx={{
        mb: 3.5,
        borderRadius: 3.5,
        bgcolor: '#ffffff',
        border: '1px solid rgba(119, 200, 199, 0.4)',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.03)',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Top Accent Gradient Line */}
      <Box sx={{ height: 4, background: 'linear-gradient(90deg, #77c8c7 0%, #936097 100%)' }} />

      <CardContent sx={{ p: { xs: 2, md: 2.8 }, pb: { xs: 2, md: 2.8 } + ' !important' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 2,
            mb: 2,
          }}
        >
          {/* Header Title & Tags */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2.5,
                bgcolor: 'rgba(119, 200, 199, 0.15)',
                color: '#0f766e',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SearchIcon size={20} />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e293b', fontSize: '1.05rem' }}>
                  {title}
                </Typography>
                {isFiltered && (
                  <Chip
                    label={`Filtered: ${filteredCount} of ${totalCount}`}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(147, 96, 151, 0.15)',
                      color: '#936097',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      borderRadius: 1.5,
                    }}
                  />
                )}
              </Box>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500, display: 'block' }}>
                {subtitle}
              </Typography>
            </Box>
          </Box>

          {/* Quick Scope Badges */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
            <Chip
              icon={<Hash size={13} style={{ color: '#0f766e' }} />}
              label="Refer ID"
              size="small"
              sx={{ bgcolor: 'rgba(119, 200, 199, 0.12)', color: '#0f766e', fontWeight: 600, fontSize: '0.72rem' }}
            />
            <Chip
              icon={<User size={13} style={{ color: '#936097' }} />}
              label="Name"
              size="small"
              sx={{ bgcolor: 'rgba(147, 96, 151, 0.12)', color: '#936097', fontWeight: 600, fontSize: '0.72rem' }}
            />
            <Chip
              icon={<Phone size={13} style={{ color: '#0f766e' }} />}
              label="Contact"
              size="small"
              sx={{ bgcolor: 'rgba(119, 200, 199, 0.12)', color: '#0f766e', fontWeight: 600, fontSize: '0.72rem' }}
            />
            <Chip
              icon={<CreditCard size={13} style={{ color: '#936097' }} />}
              label="Aadhaar"
              size="small"
              sx={{ bgcolor: 'rgba(147, 96, 151, 0.12)', color: '#936097', fontWeight: 600, fontSize: '0.72rem' }}
            />
          </Box>
        </Box>

        {/* Search Input Box */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', width: '100%' }}>
          <TextField
            fullWidth
            size="medium"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={placeholder}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon size={20} color="#77c8c7" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <Tooltip title="Clear search">
                    <IconButton size="small" onClick={() => onSearchChange('')} sx={{ color: '#94a3b8' }}>
                      <ClearIcon size={18} />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ) : null,
              sx: {
                borderRadius: 3,
                bgcolor: '#f8fafc',
                fontSize: '0.95rem',
                fontWeight: 500,
                '& fieldset': {
                  borderColor: isFiltered ? '#77c8c7' : 'rgba(226, 232, 240, 0.9)',
                  borderWidth: isFiltered ? '2px' : '1px',
                },
                '&:hover fieldset': {
                  borderColor: '#77c8c7 !important',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#936097 !important',
                  borderWidth: '2px !important',
                },
              },
            }}
          />

          {extraActions}
        </Box>

        {/* Search Feedback Summary */}
        {isFiltered && (
          <Box
            sx={{
              mt: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1,
            }}
          >
            <Typography variant="caption" sx={{ color: filteredCount > 0 ? '#0f766e' : '#ef4444', fontWeight: 700 }}>
              {filteredCount > 0
                ? `✓ Showing ${filteredCount} record${filteredCount > 1 ? 's' : ''} matching "${searchQuery}"`
                : `✕ No customer records found matching "${searchQuery}"`}
            </Typography>

            <Typography
              variant="caption"
              onClick={() => onSearchChange('')}
              sx={{
                color: '#936097',
                fontWeight: 700,
                cursor: 'pointer',
                textDecoration: 'underline',
                '&:hover': { color: '#77c8c7' },
              }}
            >
              Reset Search Filter
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomerSearchBar;
